import { FC } from 'react';
import { cn } from '@/shared/lib';
import { TruncatedText } from '@/shared/ui';
import { shownChartLabel } from '../chartValues';
import { CHART_CATEGORY_LABEL_GAP } from './chartAxis';

/** Где стоит название элемента: слева от полосы либо под столбцом. */
export type ChartCategoryPlacement = 'left' | 'bottom';

type Props = {
  /** Ширина места под подпись у полосы; под столбцом место отмеряет само деление оси */
  width: number;
  /**
   * Точка деления оси: библиотека подставляет их сама. У полос `x` — правый край места под
   * подпись: подпись кончается за промежуток до него ({@link CHART_CATEGORY_LABEL_GAP}), поэтому у
   * оси обнулены засечка и её отступ.
   */
  x?: number;
  y?: number;
  /** `offset` — половина ширины деления: по ней подпись под столбцом занимает ровно его место */
  payload?: { value?: string | number; offset?: number };
  placement?: ChartCategoryPlacement;
};

/** Высота места под одну подпись: в неё встаёт одна строка текста по середине своего деления. */
const TICK_HEIGHT = 24;

/**
 * Подпись деления оси названий: не поместившееся название усечено и раскрывается подсказкой.
 *
 * Подпись выведена разметкой внутри рисунка, а не его текстом: у текста рисунка нет ни усечения по
 * ширине, ни подсказки — обрезанное название осталось бы недоступным. Правило одно на оба
 * направления: и у полосы, и у столбца название усекается по отведённому ему месту.
 */
export const ChartCategoryTick: FC<Props> = ({ width, x = 0, y = 0, payload, placement = 'left' }) => {
  const label = shownChartLabel(String(payload?.value ?? ''));
  const isBelow = placement === 'bottom';
  // Под столбцом место подписи — ширина его деления: она известна библиотеке, а заданная ширина
  // остаётся запасной на случай, если деление своей ширины не назвало. У полосы промежуток до неё —
  // часть места под подпись: сама подпись в него не заходит.
  const belowWidth = payload?.offset ? payload.offset * 2 : width;
  const boxWidth = isBelow ? belowWidth : Math.max(0, width - CHART_CATEGORY_LABEL_GAP);

  return (
    <foreignObject
      x={isBelow ? x - boxWidth / 2 : x - width}
      y={isBelow ? y : y - TICK_HEIGHT / 2}
      width={boxWidth}
      height={TICK_HEIGHT}
    >
      <div className={cn('flex h-full items-center', isBelow ? 'justify-center px-1' : 'justify-end')}>
        <TruncatedText className="max-w-full text-xs text-muted-foreground">{label}</TruncatedText>
      </div>
    </foreignObject>
  );
};
